/**
 * Conversion + validation helpers for a domain registry's `schema.properties`,
 * used by the graphical schema editor. Pure functions — no React, no I/O.
 */

import { CSV_FORMULA_TRIGGERS } from './csvExport';

export const REGISTRY_FIELD_TYPES = [
  { value: 'string', label: 'Testo' },
  { value: 'number', label: 'Numero' },
  { value: 'boolean', label: 'Booleano' },
  { value: 'date', label: 'Data' },
  { value: 'datetime', label: 'Data e ora' },
  { value: 'time', label: 'Ora' },
  { value: 'reference', label: 'Riferimento' },
];

/**
 * Un flag di schema (`key`, `required`, `searchable`, `visible_on_ui`) e' vero come per il backend
 * (RegistrySchemas.isTrue): il booleano `true` o la stringa "true". Qualsiasi altro valore
 * (anche "false") e' falso.
 */
export const isSchemaFlagTrue = (value) => value === true || value === 'true';

/**
 * [name, def] pairs of schema.properties in authoring order. Postgres jsonb reorders object
 * keys, so the order lives in the top-level array `schema.propertyOrder` (arrays survive jsonb):
 * names listed there come first (unknown/repeated names ignored), then any property missing
 * from it. Without propertyOrder the key field (`key: true`) comes first, then the stored order.
 */
export const orderedPropertyEntries = (schema) => {
  const entries = Object.entries((schema && schema.properties) || {});
  const order = schema && Array.isArray(schema.propertyOrder) ? schema.propertyOrder : null;
  if (!order) {
    const isKey = ([, def]) => Boolean(def && isSchemaFlagTrue(def.key));
    return [...entries.filter(isKey), ...entries.filter((entry) => !isKey(entry))];
  }
  const byName = new Map(entries);
  const listed = [...new Set(order)].filter((name) => byName.has(name));
  const listedSet = new Set(listed);
  return [
    ...listed.map((name) => [name, byName.get(name)]),
    ...entries.filter(([name]) => !listedSet.has(name)),
  ];
};

/** schema.properties map -> ordered editor rows (no synthetic id; the component adds one). */
export const propertiesToRows = (schema) =>
  orderedPropertyEntries(schema).map(([key, def]) => {
    const d = def || {};
    const ref = d.reference || {};
    return {
      key,
      name: d.name || '',
      type: d.type || 'string',
      required: isSchemaFlagTrue(d.required),
      searchable: isSchemaFlagTrue(d.searchable),
      visibleOnUi: isSchemaFlagTrue(d.visible_on_ui),
      isKey: isSchemaFlagTrue(d.key),
      maxLength: d.maxLength != null ? String(d.maxLength) : '',
      pattern: d.pattern || '',
      enumText: Array.isArray(d.enum) ? d.enum.join(', ') : '',
      description: d.description || '',
      reference: { registryId: ref.registryId || '', field: ref.field || '' },
    };
  });

/** editor rows -> schema object (preserving other top-level keys of baseSchema), with propertyOrder = row order. */
export const rowsToSchema = (rows, baseSchema = {}) => {
  const properties = {};
  (rows || []).forEach((row) => {
    const key = (row.key || '').trim();
    if (!key) return;
    const def = { type: row.type || 'string' };
    if (row.name && row.name.trim()) def.name = row.name.trim();
    if (row.required) def.required = true;
    if (row.searchable) def.searchable = true;
    if (row.visibleOnUi) def.visible_on_ui = true;
    if (row.isKey) def.key = true;
    const maxLenText = String(row.maxLength ?? '').trim();
    if (maxLenText !== '') {
      const ml = parseInt(maxLenText, 10);
      if (!Number.isNaN(ml)) def.maxLength = ml;
    }
    if (row.pattern && row.pattern.trim()) def.pattern = row.pattern.trim();
    const enumVals = (row.enumText || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (enumVals.length) def.enum = enumVals;
    if (row.description && row.description.trim()) def.description = row.description.trim();
    if (row.type === 'reference') {
      def.reference = {
        registryId: (row.reference && row.reference.registryId ? row.reference.registryId : '').trim(),
        field: (row.reference && row.reference.field ? row.reference.field : '').trim(),
      };
    }
    properties[key] = def;
  });
  const base = baseSchema || {};
  return { ...base, propertyOrder: Object.keys(properties), properties };
};

/** Validation for the whole row set. Returns [{index, key, message}]. */
export const registrySchemaErrors = (rows) => {
  const errors = [];
  const seen = new Set();
  let keyCount = 0;
  (rows || []).forEach((row, index) => {
    const key = (row.key || '').trim();
    if (!key) {
      errors.push({ index, key: row.key || '', message: 'La chiave è obbligatoria' });
    } else {
      if (/\s/.test(key)) {
        errors.push({ index, key, message: 'La chiave non può contenere spazi' });
      }
      if (/[\u0000-\u001F\u007F-\u009F]/.test(key) || CSV_FORMULA_TRIGGERS.includes(key[0])) {
        errors.push({
          index,
          key,
          message: "La chiave non può iniziare con = + - @ ' né contenere caratteri di controllo",
        });
      }
      if (seen.has(key)) {
        errors.push({ index, key, message: `Chiave duplicata: ${key}` });
      }
      seen.add(key);
    }
    if (row.isKey) keyCount += 1;
    if (row.type === 'reference') {
      const ref = row.reference || {};
      if (!ref.registryId || !ref.field) {
        errors.push({ index, key, message: 'Il riferimento richiede anagrafica e campo' });
      }
    }
    const maxLenText = String(row.maxLength ?? '').trim();
    if (maxLenText !== '') {
      const ml = Number(maxLenText);
      if (!Number.isInteger(ml) || ml <= 0) {
        errors.push({ index, key, message: 'maxLength deve essere un intero positivo' });
      }
    }
  });
  if (keyCount > 1) {
    errors.push({ index: -1, key: '', message: 'Solo un campo può essere la chiave' });
  }
  return errors;
};
